import { Fragment, useEffect, useRef, useState } from 'react'
import { ChevronRight, Copy } from 'lucide-react'
import type { Person, Share } from '@/api'
import { copy } from '@/copy'
import { clock, remaining } from '@/lib/time'
import { writeClipboard } from '@/lib/clipboard'
import { useConsole } from '@/lib/store'

interface Props {
  share: Share
  now: number
  onStop: (share: Share) => Promise<boolean>
}

/** Under this much time left, the time turns amber: the share is about to stop. */
const soon = 5 * 60_000

// One live share as one line: its app, the device it runs from, who gets in,
// the time left and what you can do. Everything else opens under the line
// from the app's name, in one panel of sections split by hairlines: the
// people named, each with whether they opened it; every app it serves; when
// it started and stops. A share anyone with the link can open has nobody to
// list, and its line says so. The link is copied, never shown, and neither
// is the share's id: it is the link's own hostname (D-014).
export function ShareCard({ share, now, onStop }: Props) {
  const [open, setOpen] = useState(false)
  const [app, ...more] = share.apps
  const created = Date.parse(share.created_at)
  const expires = Date.parse(share.expires_at)
  const left = Math.max(0, expires - now)
  const lit = expires > created ? Math.min(1, left / (expires - created)) : 0
  const paused = share.state !== 'ready'
  const named = share.people.length > 0
  const opened = share.people.filter((p) => p.opened_at).length
  const details = `share-details-${share.label}`

  return (
    <article className="share-row" data-open={open || undefined} aria-labelledby={`share-${share.label}`}>
      <h2 id={`share-${share.label}`} className="share-app m-0 min-w-0 text-[15px] font-normal leading-[22px]">
        <button type="button" className="disclose" aria-expanded={open} aria-controls={details} onClick={() => setOpen((o) => !o)}>
          <ChevronRight className="chevron" aria-hidden="true" />
          <span className={`dot ${paused ? 'text-attention' : 'text-working'}`} aria-hidden="true" />
          <span className="min-w-0 flex-1">
            <span className="font-mono font-medium">
              <Host name={app} />
            </span>
            {more.length > 0 && ' '}
            {more.length > 0 && (
              <span className="ml-1 whitespace-nowrap text-[13px] text-secondary">
                <span aria-hidden="true">{copy.shares.moreApps(more.length)}</span>
                <span className="visually-hidden"> {copy.shares.moreAppsSpoken(more.length)}</span>
              </span>
            )}
            {paused && ' '}
            {paused ? (
              <span className="ml-1 whitespace-nowrap text-[13px] font-medium text-attention">
                <span className="visually-hidden">, </span>
                {copy.status[share.state]}
              </span>
            ) : (
              <span className="visually-hidden">, {copy.status[share.state]}</span>
            )}
          </span>
        </button>
      </h2>
      <div className="share-meta">
        <p className="share-from m-0 min-w-0 [overflow-wrap:anywhere]">
          <span className="visually-hidden">{copy.shares.from} </span>
          {share.device.name}
        </p>
        <p className="share-who m-0 min-w-0">
          <span className="visually-hidden">{copy.shares.whoGetsIn}: </span>
          {named ? copy.shares.access(share.people.length, opened) : copy.shares.anyone}
        </p>
      </div>
      <p className={`share-time m-0 ${paused ? 'time time-paused' : 'time'}`} title={copy.shares.stops(clock(new Date(expires)))}>
        <span className="time-track w-10 flex-none" aria-hidden="true">
          <span className="time-lit" style={{ width: `${(lit * 100).toFixed(2)}%` }} />
        </span>
        <span className={`whitespace-nowrap font-mono text-[13px] font-medium tabular-nums ${left <= soon ? 'text-attention' : ''}`}>
          {copy.shares.left(remaining(left))}
        </span>
      </p>
      <ShareActions share={share} onStop={onStop} />
      <div id={details} className="share-details" data-named={named || undefined} hidden={!open}>
        {named && (
          <section className="detail" aria-labelledby={`${details}-people`}>
            <h3 id={`${details}-people`} className="detail-label">
              {copy.shares.people}
            </h3>
            <ul className="people">
              {share.people.map((p) => (
                <PersonLine key={p.email} person={p} />
              ))}
            </ul>
          </section>
        )}
        <section className="detail" aria-labelledby={`${details}-serves`}>
          <h3 id={`${details}-serves`} className="detail-label">
            {copy.shares.serves}
          </h3>
          <ul className="hosts">
            {share.apps.map((a) => (
              <li key={a}>
                <Host name={a} />
              </li>
            ))}
          </ul>
        </section>
        <section className="detail" aria-labelledby={`${details}-time`}>
          <h3 id={`${details}-time`} className="detail-label">
            {copy.shares.time}
          </h3>
          <dl className="times">
            <dt>{copy.shares.started}</dt>
            <dd>{clock(new Date(created))}</dd>
            <dt>{copy.shares.stopsAt}</dt>
            <dd>{clock(new Date(expires))}</dd>
          </dl>
        </section>
      </div>
    </article>
  )
}

/** A run of text that may wrap before each of its dots, and only then anywhere. */
function Dotted({ text }: { text: string }) {
  return (
    <>
      {text.split(/(?=\.)/).map((part, i) => (
        <Fragment key={i}>
          {i > 0 && <wbr />}
          {part}
        </Fragment>
      ))}
    </>
  )
}

/** An email that, when it must wrap, breaks before its @ first: the @ and
 *  the domain move whole to the next line, and only a domain wider than the
 *  line breaks inside, before a dot. */
function Email({ address }: { address: string }) {
  const at = address.lastIndexOf('@')
  if (at < 0) return <Dotted text={address} />
  return (
    <>
      <Dotted text={address.slice(0, at)} />
      <span className="inline-block max-w-full [overflow-wrap:anywhere]">
        <Dotted text={address.slice(at)} />
      </span>
    </>
  )
}

/** A host that wraps only after its dots, never at a hyphen inside a name. */
function Host({ name }: { name: string }) {
  const parts = name.split('.')
  return (
    <>
      {parts.map((part, i) => (
        <Fragment key={i}>
          <span className="whitespace-nowrap">
            {part}
            {i < parts.length - 1 && '.'}
          </span>
          {i < parts.length - 1 && <wbr />}
        </Fragment>
      ))}
    </>
  )
}

function PersonLine({ person }: { person: Person }) {
  const opened = person.opened_at ? Date.parse(person.opened_at) : 0
  const failed = !opened && person.invite === 'not_sent'
  const state = opened ? copy.shares.opened(clock(new Date(opened))) : failed ? copy.shares.notSent : person.invite === 'sent' ? copy.shares.invited : ''
  const tone = opened ? 'opened' : failed ? 'failed' : 'waiting'
  return (
    <li className="person">
      <span className="person-email">
        <Email address={person.email} />
      </span>
      {state && (
        <span className={`person-state ${tone}`}>
          <span className="state-dot" aria-hidden="true" />
          {state}
        </span>
      )}
    </li>
  )
}

function ShareActions({ share, onStop }: { share: Share; onStop: Props['onStop'] }) {
  const [copied, setCopied] = useState<'idle' | 'copied' | 'failed'>('idle')
  const [asking, setAsking] = useState(false)
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)
  const cancel = useRef<HTMLButtonElement>(null)
  const stopButton = useRef<HTMLButtonElement>(null)
  const hold = useConsole((s) => s.hold)
  const release = useConsole((s) => s.release)
  const announce = useConsole((s) => s.announce)

  useEffect(() => {
    if (copied === 'idle') return
    const t = setTimeout(() => setCopied('idle'), 2000)
    return () => clearTimeout(t)
  }, [copied])

  // The refresh holds while the question is open, so the row stays put.
  useEffect(() => {
    if (!asking) return
    hold()
    cancel.current?.focus()
    return release
  }, [asking, hold, release])

  const onCopy = async () => {
    const ok = await writeClipboard(share.link)
    setCopied(ok ? 'copied' : 'failed')
    announce(ok ? copy.shares.copied : copy.shares.copyFailed)
  }
  const ask = () => {
    setFailed(false)
    setAsking(true)
  }
  const close = () => {
    setAsking(false)
    requestAnimationFrame(() => stopButton.current?.focus())
  }
  const confirm = async () => {
    if (busy) return
    setBusy(true)
    const ok = await onStop(share)
    setBusy(false)
    if (!ok) {
      setFailed(true)
      close()
    }
  }

  const askId = `ask-${share.label}`
  return (
    <>
      <div className="share-actions" onKeyDown={asking ? (e) => e.key === 'Escape' && close() : undefined}>
        {asking ? (
          <>
            <p id={askId} className="visually-hidden">
              {copy.shares.ask}
            </p>
            <button type="button" className="button danger" onClick={confirm} aria-describedby={askId} aria-disabled={busy || undefined}>
              {busy ? copy.shares.stopping : copy.shares.stopShare}
              <span className="visually-hidden"> {share.apps[0]}</span>
            </button>
            <button type="button" className="ghost" ref={cancel} onClick={close}>
              {copy.cancel}
            </button>
          </>
        ) : (
          <>
            <button type="button" className="action" onClick={onCopy}>
              <Copy className="h-4 w-4" aria-hidden="true" />
              {copied === 'copied' ? copy.shares.copied : copied === 'failed' ? copy.shares.copyFailed : copy.shares.copy}
              <span className="visually-hidden"> {share.apps[0]}</span>
            </button>
            <button type="button" className="ghost" ref={stopButton} onClick={ask}>
              {copy.shares.stop}
              <span className="visually-hidden"> {share.apps[0]}</span>
            </button>
          </>
        )}
      </div>
      {failed && (
        <p className="share-note m-0 text-[14px] leading-5 text-failed" role="status">
          {copy.shares.stopFailed}
        </p>
      )}
    </>
  )
}
